import { FillInTheBlanksAreaPinComponentSettings, getBlanksAndStripContent } from '@lessonup/pins-shared';
import { color, cx, elevationShadow, styled } from '@lessonup/ui-components';
import React, { useMemo } from 'react';
import { TipTapComponentPropsWithScale } from '../../components/Tiptap/Tiptap.utils';
import { computeOpacity } from '../../utils';
import { useEffectivePinComponentSettings } from '../../utils/pinTheme/PinThemeContext';
import { useTextPinComponentHandlers } from '../TextPinComponent/hooks/useTextPinComponentHandlers';
import {
  AreaPinComponentWrapper,
  computeFillInTheBlanksAreaPinComponentStyles,
  TipTapWrapper,
  WordBankWrapper,
} from './FillInTheBlanksAreaPinComponent.utils';
import { FillInTheBlanksTiptapGenerateHtml } from './FillInTheBlanksTiptapGenerateHtml';
import {
  FillInTheBlanksWordBank,
  FillInTheBlanksWordBankDistractor,
  FillInTheBlanksWordBankItem,
} from './FillInTheBlanksWordBank';

export interface FillInTheBlanksAreaPinComponentEditorProps {
  settings: FillInTheBlanksAreaPinComponentSettings;
  active?: boolean;
  pinId: string;
  pinComponentId: string;
  Renderer?: React.FC<
    TipTapComponentPropsWithScale & { settings: FillInTheBlanksAreaPinComponentSettings; isPreviewMode?: boolean }
  >;
  isTextEditMode?: boolean;
  setModeTextEdit?: (textPinComponentId: string) => void;
  scale?: number;
  isPreviewMode?: boolean;
  editable?: boolean;
}

export const FillInTheBlanksAreaPinComponentEditor: React.FC<FillInTheBlanksAreaPinComponentEditorProps> = ({
  settings,
  active = false,
  pinId,
  pinComponentId,
  Renderer,
  isTextEditMode,
  setModeTextEdit,
  scale,
  isPreviewMode,
  editable = true,
}) => {
  const {
    registerEditor,
    // textPinComponentRef,
    handleMouseDown,
    // handleTouchStart,
    handleMouseUpTouchEnd,
    handleMouseMoveTouchMove,
    // handleOnContextMenu,
  } = useTextPinComponentHandlers({
    active,
    isTextEditMode: isTextEditMode ?? false,
    setModeTextEdit: setModeTextEdit,
    componentId: pinComponentId,
  });
  const fillInTheBlanksSettings = useEffectivePinComponentSettings('FILL_IN_THE_BLANKS_AREA', {
    settings,
  });

  return (
    <FillInTheBlanksAreaPinComponentWrapper
      settings={fillInTheBlanksSettings}
      handleMouseDown={editable ? handleMouseDown : undefined}
      handleMouseMoveTouchMove={editable ? handleMouseMoveTouchMove : undefined}
      handleMouseUpTouchEnd={editable ? handleMouseUpTouchEnd : undefined}
      active={active}
      isTextEditMode={isTextEditMode}
    >
      {Renderer ? (
        <Renderer
          pinId={pinId}
          pinComponentId={pinComponentId}
          registerEditor={registerEditor}
          editable={editable}
          active={active}
          isTextEditMode={isTextEditMode}
          scale={scale}
          settings={fillInTheBlanksSettings}
          isPreviewMode={isPreviewMode}
        />
      ) : null}
    </FillInTheBlanksAreaPinComponentWrapper>
  );
};

export interface FillInTheBlanksPinComponentProps {
  settings: FillInTheBlanksAreaPinComponentSettings;
  active?: boolean;
  pinId: string;
  pinComponentId: string;
  isTextEditMode?: boolean;
}

/** This is the version used for thumbnail rendering */
export const FillInTheBlanksAreaPinComponent: React.FC<FillInTheBlanksPinComponentProps> = ({
  settings,
  active,
  isTextEditMode,
}) => {
  const fillInTheBlanksSettings = useEffectivePinComponentSettings('FILL_IN_THE_BLANKS_AREA', {
    settings,
  });

  const { blanks } = useMemo(
    () => getBlanksAndStripContent(fillInTheBlanksSettings.tipTapContent ?? {}),
    [fillInTheBlanksSettings.tipTapContent]
  );

  const items = useMemo(() => {
    const blanksItems: FillInTheBlanksWordBankItem[] = blanks.map((blank) => ({
      type: 'blank',
      word: blank,
    }));
    const distractors: FillInTheBlanksWordBankDistractor[] = fillInTheBlanksSettings.distractors.map((distractor) => ({
      type: 'distractor',
      word: distractor,
    }));
    return [...blanksItems, ...distractors];
  }, [blanks, fillInTheBlanksSettings.distractors]);

  return (
    <FillInTheBlanksAreaPinComponentWrapper
      settings={fillInTheBlanksSettings}
      isTextEditMode={isTextEditMode}
      active={active}
    >
      <AreaPinComponentWrapper>
        <TipTapWrapper backgroundColor={fillInTheBlanksSettings.backgroundColor}>
          <FillInTheBlanksTiptapGenerateHtml content={fillInTheBlanksSettings.tipTapContent ?? {}} />
        </TipTapWrapper>
        <WordBankWrapper>
          <FillInTheBlanksWordBank items={items} />
        </WordBankWrapper>
      </AreaPinComponentWrapper>
    </FillInTheBlanksAreaPinComponentWrapper>
  );
};

interface WrapperProps {
  settings: FillInTheBlanksAreaPinComponentSettings;
  children?: React.ReactNode;
  handleMouseDown?: (event: React.MouseEvent) => void;
  handleMouseMoveTouchMove?: (event: React.MouseEvent | React.TouchEvent) => void;
  handleMouseUpTouchEnd?: (event: React.MouseEvent | React.TouchEvent) => void;
  active?: boolean;
  isTextEditMode?: boolean;
}

export const FillInTheBlanksAreaPinComponentWrapper: React.FC<WrapperProps> = ({
  settings,
  children,
  handleMouseDown,
  handleMouseMoveTouchMove,
  handleMouseUpTouchEnd,
  active,
  isTextEditMode,
}) => {
  const fillInTheBlanksSettings = useEffectivePinComponentSettings('FILL_IN_THE_BLANKS_AREA', {
    settings,
  });
  const styles = computeFillInTheBlanksAreaPinComponentStyles(fillInTheBlanksSettings);

  return (
    <StyledFillInTheBlanksWrapper
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMoveTouchMove}
      onTouchMove={handleMouseMoveTouchMove}
      onMouseUp={handleMouseUpTouchEnd}
      onTouchEnd={handleMouseUpTouchEnd}
      style={styles}
      className={cx(active ? 'active' : undefined, isTextEditMode ? 'edit' : undefined)}
    >
      <StyledFillInTheBlankBackground
        style={{
          background: 'transparent',
          opacity: computeOpacity(fillInTheBlanksSettings.transparency),
        }}
      >
        {children}
      </StyledFillInTheBlankBackground>
    </StyledFillInTheBlanksWrapper>
  );
};

const StyledFillInTheBlanksWrapper = styled.div`
  text-align: var(--component-horizontal-align);
  align-items: var(--component-vertical-align);
  width: 100%;
  height: 100%;
  display: grid;
  word-break: break-word;
  white-space: break-spaces;
  font-family: var(--component-fontfamily);
  font-size: var(--component-fontsize);
  color: var(--component-fontcolor);
  font-weight: var(--component-fontweight);
  font-style: var(--component-fontstyle);

  &.edit {
    *:focus .selected-text {
      background-color: initial;
    }
    .selected-text {
      background-color: ${color.additional.disabled.background};
    }
  }

  &:not(.edit) {
    cursor: default;
    caret-color: transparent;
    span::selection {
      background-color: transparent;
      color: inherit;
    }
  }

  sup {
    vertical-align: top;
    font-size: 0.6em;
  }

  sub {
    vertical-align: bottom;
    font-size: 0.6em;
  }

  .ProseMirror:focus {
    outline: none;
  }

  ul,
  ol {
    display: flex;
    flex-direction: column;
    align-items: var(--component-list-align);
    justify-content: var(--component-list-justify);
    margin: 0;
    padding: 0;

    li {
      margin-left: var(--tiptap-listitem-marker-fontsize, var(--component-fontsize, 24px));
      font-variant-numeric: tabular-nums;

      &::marker {
        color: var(--tiptap-listitem-marker-color, var(--component-fontcolor));
        font-size: var(--tiptap-listitem-marker-fontsize, var(--component-fontsize));
        font-family: var(--tiptap-listitem-marker-fontfamily, var(--component-fontfamily));
        font-weight: var(--tiptap-listitem-marker-fontweight, var(--component-fontweight));
        font-style: var(--tiptap-listitem-marker-fontstyle, var(--component-fontstyle));
      }
    }
  }

  &.shadow-hard {
    text-shadow: ${elevationShadow.e100};
  }
  &.shadow-medium {
    text-shadow: ${elevationShadow.e200};
  }
  &.shadow-soft {
    text-shadow: ${elevationShadow.e300};
  }

  p {
    margin: 0;
    padding: 0;

    &:not(:first-of-type) {
      padding-top: var(--component-paragraph-spacing);
    }
  }

  .ProseMirror p.is-editor-empty:first-of-type::before {
    display: inline-block;
    position: absolute;
    color: #292e3d;
    content: var(--component-placeholder-text);
    font-weight: var(--component-fontweight);
    pointer-events: none;
    left: var(--component-placeholder-left);
    right: var(--component-placeholder-right);
    transform: var(--component-placeholder-transform);
  }

  .collaboration-cursor__caret {
    border-left: 0.5px solid #0d0d0d;
    border-right: 0.5px solid #0d0d0d;
    margin-left: -0.5px;
    margin-right: -0.5px;
    pointer-events: none;
    position: relative;
    word-break: normal;
  }

  /* Render the username above the caret */
  .collaboration-cursor__label {
    border-radius: 1.5px 1.5px 1.5px 0;
    color: #ffffff;
    font-size: 6px;
    font-style: normal;
    font-weight: 600;
    left: -0.5px;
    line-height: normal;
    padding: 0.1rem 0.3rem;
    position: absolute;
    top: -0.7em;
    user-select: none;
    white-space: nowrap;
  }
`;

const StyledFillInTheBlankBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;
